import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import baseUrl from "../../config/baseUrl";
import StatusPopup from "./StatusPopup";
import EditDocumentRequestSettingsPopup from "./EditDocumentRequestSettingsPopup";
import DeleteDocumentRequestSettingsPopup from "./DeleteDocumentRequestSettingsPopup";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import documentRequestSettingsData from "../../api/documentRequestSettings";
import AddDocumentRequestSettings from "./AddDocumentRequestSettings";
import getAllTypeList from "../../api/getAllFieldTypeList";
import "../../styles/Table.css";
import BulkDeletePopup from "./BulkDeletePopup";
import TableSkeleton from "../common/TableSkeleton";
import PaginationSection from "../common/PaginationSection";
import DataToolbar from "../common/DataToolbar";

import {
  DataGrid,
  GridToolbar,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid/";

import Box from "@mui/material/Box";

import {
  CaretSortIcon,
  ChevronDownIcon,
  StarIcon,
  StarFilledIcon,
} from "@radix-ui/react-icons";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn-components/ui/select";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "../../shadcn-components/ui/button";
import { Checkbox } from "../../shadcn-components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../shadcn-components/ui/dropdown-menu";
import { Input } from "../../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../shadcn-components/ui/table";
import { fileIcon } from "../common/FileIcons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../shadcn-components/ui/tooltip";
import { useDebounce } from "use-debounce";
import { convertToUTC, formatDate } from "../../utils/formatDate";
import { format } from "date-fns";
import { useExcelExport } from "../../hooks/useExcelExport";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

/*** Component for listing all the document requests settings ***/
const DocumentRequestSettingsTable = ({
  addDocumentTab,
  showAddDocumentRequestSettingsForm,
  onAddDocumentRequestSettingsButtonClick,
  typeOptions,
  setTypeOptions,
  canEditDocumentRequestTemplate,
  canDeleteDocumentRequestTemplate,
  shouldShowActionColumn,
}) => {
  const [loading, setLoading] = useState(false);
  const type = "client_type";
  const [documentRequestSettingsAdded, setDocumentRequestSettingsAdded] =
    useState(0);

  const { token, user_id, is_admin } = useContext(AuthContext);
  const [documentRequestSettingsResponse, setDocumentRequestSettingsResponse] =
    useState([]);

  const [bulkDeleteRequestSetting, setBulkDeleteRequestSetting] =
    useState(false);
  const [bulkDeleteRequestSettingIds, setBulkDeleteRequestSettingIds] =
    useState();

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);

  const [selectAllChecked, setSelectAllChecked] = useState();

  const [showFilterButton, setShowFilterButton] = useState(false);
  // For date filters
  const [updatedAt, setUpdatedAt] = useState(null);

  // ✅ State for downloading all data
  const [downloadingAll, setDownloadingAll] = useState(false);

  const prevFilterParamsRef = useRef({});
  const prevStateRef = useRef({
    updatedAt,
    debouncedGlobalFilter,
    columnFilters,
  });

  const DocumentRequestSettingsResponseFunc = async () => {
    setLoading(true);
    const offset = currentPage - 1;
    const filterParams = {};

    columnFilters.forEach((filterItem) => {
      filterParams[filterItem.id] = filterItem.value;
    });

    const filtersChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);

    // If filters changed, reset page to 1
    if (filtersChanged) {
      setCurrentPage(1);
    }

    prevFilterParamsRef.current = filterParams;

    const sortParam = sorting
      .map((sortItem) => {
        return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
      })
      .join(",");

    const params = {
      isAdmin: is_admin,
      userId: user_id,
      limit: pageSize,
      offset: offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,

      ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${baseUrl}/api/get-document-req-settings-data?${queryString}`;
    const response = await documentRequestSettingsData({ token, api: url });
    if (response.code == 200) {
      setTotalPages(Math.ceil(response?.data[0].document_count / pageSize));

      setDocumentRequestSettingsResponse(response?.data[0].all_document);
    } else {
      setTotalPages(0);
      setDocumentRequestSettingsResponse([]);
    }
    setLoading(false);
  };

  // ✅ Get export hook
  const { exportToExcel } = useExcelExport();

  // ✅ Map of API field names to display names
  const fieldMapping = {
    document_request_setting_id: "Document ID",
    document_name: "Document Name",
    document_name_es: "Document Name (ES)",
    document_desc: "Document Description",
    client_type: "Client Type ID",
    fields_name: "Client Type",
    request_type: "Request Type",
    sample_document_link: "Sample Document Link",
    document_type: "Document Type",
    ocr_status: "OCR Status",
    modified_by: "Modified By",
    service_sub_type_c: "Service Sub Type ID",
    status: "Status",
    last_status_updated_date: "Last Status Updated",
    created_at: "Created At",
    updated_at: "Modified At",
    client_subtype: "Client Sub Type",
  };

  // ✅ Get visible column field names
  const getVisibleFields = () => {
    return table
      .getAllColumns()
      .filter((column) => column.getIsVisible() && column.columnDef.accessorKey)
      .map((column) => column.columnDef.accessorKey);
  };

  // ✅ Prepare export data with visible columns only
  const prepareExportData = (data) => {
    const visibleFields = getVisibleFields();

    return data.map((item) => {
      const rowData = {};
      visibleFields.forEach((field) => {
        const displayName = fieldMapping[field] || field;
        rowData[displayName] = item[field] || "";
      });
      return rowData;
    });
  };

  // ✅ Download current page
  const handleDownloadCurrentPage = () => {
    if (documentRequestSettingsResponse.length === 0) {
      alert("No data to export on current page");
      return;
    }
    const exportData = prepareExportData(documentRequestSettingsResponse);
    exportToExcel(
      exportData,
      "document-request-settings-current-page",
      "Current Page",
    );
  };

  // ✅ Download selected rows
  const handleDownloadSelectedRows = () => {
    const selectedRows = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    if (selectedRows.length === 0) {
      alert("Please select rows to download");
      return;
    }
    const exportData = prepareExportData(selectedRows);
    exportToExcel(
      exportData,
      "document-request-settings-selected",
      "Selected Rows",
    );
  };

  // ✅ Download all data
  const handleDownloadAllData = async () => {
    setDownloadingAll(true);
    try {
      const url = `${baseUrl}/api/get-document-req-settings-data?isAdmin=${is_admin}&userId=${user_id}`;
      const response = await documentRequestSettingsData({ token, api: url });

      if (response.code === 200 && response.data && response.data[0]) {
        const allData = response.data[0].all_document || [];
        if (allData.length === 0) {
          alert("No data available to download");
          setDownloadingAll(false);
          return;
        }
        const exportData = prepareExportData(allData);
        exportToExcel(exportData, "document-request-settings-all", "All Data");
      } else {
        alert("Failed to fetch all data");
      }
    } catch (error) {
      console.error("Error downloading all data:", error);
      alert("Error downloading data");
    } finally {
      setDownloadingAll(false);
    }
  };

  const clientTypeLoad = async () => {
    const data = await getAllTypeList({ token, type });
    if (data.code == 200) {
      const filteredTypeList =
        data?.data !== null
          ? data?.data.map((type) => {
              return {
                label: type.fields_name,
                typeId: type.field_setting_id,
              };
            })
          : [];
      setTypeOptions(filteredTypeList);
    } else {
      setTypeOptions([]);
    }
  };

  const _initFetched = React.useRef(false);
  useEffect(() => {
    if (_initFetched.current) return;
    _initFetched.current = true;
    clientTypeLoad();
  }, []);

  useEffect(() => {
    const currentState = {
      updatedAt,
      debouncedGlobalFilter,
      columnFilters,
    };

    // If filters or sorting change, reset page to 1
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return; // Avoid double API call
      }
    }

    prevStateRef.current = currentState;
    DocumentRequestSettingsResponseFunc();
    setSelectAllChecked(false);
    setRowSelection({}); // ✅ Reset selection on page change
  }, [
    documentRequestSettingsAdded,
    bulkDeleteRequestSetting,
    debouncedGlobalFilter,
    currentPage,
    columnFilters,
    pageSize,
    sorting,
    updatedAt,
  ]);

  const setDocumentRequestSettingsAddedToTrue = () => {
    setDocumentRequestSettingsAdded((count) => count + 1);
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    const ids = selectedRowsData
      .map((row) => row.document_request_setting_id)
      .join(",");
    if (actionType === "delete") {
      setBulkDeleteRequestSettingIds(ids);
      setBulkDeleteRequestSetting(true);
    }
  };

  // ✅ COLUMNS ARRAY WITH ALL 17 API FIELDS
  // Add this to replace the current columns array in your component

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() ||
            table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon className="" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("delete")}
                  disabled={!canDeleteDocumentRequestTemplate}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            setSelectAllChecked(true);
            row.toggleSelected(!!value);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // ✅ Column 1: Document Name
    {
      accessorKey: "document_name",
      displayName: "Document Request Name",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Document Request Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div
          className={
            row.original.sample_document_link
              ? "capitalize text-blue-500 underline cursor-pointer"
              : "capitalize"
          }
          {...(row.original.sample_document_link
            ? { onClick: () => addDocumentTab(row.original) }
            : {})}
        >
          {row.getValue("document_name")}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 2: Document Name (ES)
    {
      accessorKey: "document_name_es",
      displayName: "Document Name (ES)",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Document Name (ES)
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">
          {row.getValue("document_name_es") || "—"}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 3: Document Description
    // {
    //   accessorKey: "document_desc",
    //   displayName: "Document Description",
    //   filterable: true,
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Document Description
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => (
    //     <div className="text-sm max-w-xs truncate">
    //       {row.getValue("document_desc") || "—"}
    //     </div>
    //   ),
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },

    // ✅ Column 4: Client Type (Fields Name)
    {
      accessorKey: "fields_name",
      displayName: "Client Type",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Type
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("fields_name")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 5: Client Sub Type
    {
      accessorKey: "client_subtype",
      displayName: "Client Sub Type",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Sub Type
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("client_subtype")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 6: Request Type
    {
      accessorKey: "request_type",
      displayName: "Request Type",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Request Type
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("request_type")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 7: OCR Status
    // {
    //   accessorKey: "ocr_status",
    //   displayName: "OCR Status",
    //   filterable: true,
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       OCR Status
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => (
    //     <div className="capitalize">{row.getValue("ocr_status") || "—"}</div>
    //   ),
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },

    // ✅ Column 8: Status
    // {
    //   accessorKey: "status",
    //   displayName: "Status",
    //   filterable: true,
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Status
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => (
    //     <div className="capitalize">{row.getValue("status")}</div>
    //   ),
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },

    // ✅ Column 9: Last Status Updated
    // {
    //   accessorKey: "last_status_updated_date",
    //   displayName: "Last Status Updated",
    //   filterable: true,
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Last Status Updated
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => {
    //     const date = row.getValue("last_status_updated_date");
    //     return <div className="text-sm">{date ? formatDate(date) : "—"}</div>;
    //   },
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },

    // ✅ Column 10: Created At
    // {
    //   accessorKey: "created_at",
    //   displayName: "Created At",
    //   filterable: true,
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Created At
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => {
    //     const date = row.getValue("created_at");
    //     return <div className="text-sm">{date ? formatDate(date) : "—"}</div>;
    //   },
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },

    // ✅ Column 11: Modified At
    {
      accessorKey: "updated_at",
      displayName: "Modified At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Modified At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const updated = row.getValue("updated_at");
        return (
          <div className="capitalize text-wrap">{formatDate(updated)}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    // ✅ Column 12: Actions (Keep at end)
    {
      id: "actions",
      header: ({ column }) => {
        if (!shouldShowActionColumn) {
          return null;
        }
        return (
          <Button variant="ghost" className="p-0 !cursor-default">
            Actions
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="flex gap-1.5">
          {canEditDocumentRequestTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <StatusPopup
                      setDocumentRequestSettingsAddedToTrue={
                        setDocumentRequestSettingsAddedToTrue
                      }
                      tableValues={row.original}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Change Status</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canEditDocumentRequestTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <EditDocumentRequestSettingsPopup
                      setDocumentRequestSettingsAddedToTrue={
                        setDocumentRequestSettingsAddedToTrue
                      }
                      tableValues={row.original}
                      typeOptions={typeOptions}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Edit</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canDeleteDocumentRequestTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <DeleteDocumentRequestSettingsPopup
                      setDocumentRequestSettingsAddedToTrue={
                        setDocumentRequestSettingsAddedToTrue
                      }
                      id={row.original.document_request_setting_id}
                      setRowSelection={setRowSelection}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
      enableHiding: false,
    },
  ];

  const table = useReactTable({
    data: documentRequestSettingsResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pageSize: 20,
    },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
    },

    globalFilterFn: (rows, columnFilters) => {
      const [globalFilter] = columnFilters.filter(
        (filter) => filter.id === "global",
      );
      if (!globalFilter || !globalFilter.value) return rows;

      return rows.filter((row) => {
        const name = row.original.name.toLowerCase(); // Assuming 'name' is the column key
        return name.includes(globalFilter.value.toLowerCase());
      });
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue); // Update the global filter state
    },
  });

  return (
    <>
      {bulkDeleteRequestSetting ? (
        <BulkDeletePopup
          RequestSettingbulkid={bulkDeleteRequestSettingIds}
          bulkDeleteRequestSetting={bulkDeleteRequestSetting}
          setBulkDeleteRequestSetting={setBulkDeleteRequestSetting}
          setRowSelection={setRowSelection}
        />
      ) : null}
      <div className="user-table">
        {showAddDocumentRequestSettingsForm ? (
          <AddDocumentRequestSettings
            typeOptions={typeOptions}
            onAddDocumentRequestSettingsButtonClick={
              onAddDocumentRequestSettingsButtonClick
            }
            setDocumentRequestSettingsAddedToTrue={
              setDocumentRequestSettingsAddedToTrue
            }
          />
        ) : (
          <div className="document__table mt-3">
            <div className="w-full bg-white px-2">
              <div className="flex justify-between items-center py-2 flex-wrap gap-4">
                <Input
                  placeholder="Search..."
                  value={globalFilter}
                  onChange={(event) =>
                    table.setGlobalFilter(event.target.value)
                  }
                  className="max-w-sm"
                />
                <div className="flex gap-2">
                  {/* ✅ DOWNLOAD DROPDOWN MENU */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={documentRequestSettingsResponse.length === 0}
                      >
                        <FileDownloadIcon className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onSelect={handleDownloadCurrentPage}
                        disabled={documentRequestSettingsResponse.length === 0}
                      >
                        📄 Current Page
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onSelect={handleDownloadSelectedRows}
                        disabled={
                          table.getFilteredSelectedRowModel().rows.length === 0
                        }
                      >
                        ✓ Selected Rows (
                        {table.getFilteredSelectedRowModel().rows.length})
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onSelect={handleDownloadAllData}
                        disabled={downloadingAll}
                      >
                        {downloadingAll
                          ? "⏳ Downloading All..."
                          : "📊 All Data"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {/* ✅ END DOWNLOAD DROPDOWN MENU */}

                  <Button
                    variant="outline"
                    className=""
                    onClick={() => setShowFilterButton(!showFilterButton)}
                  >
                    <FilterListIcon sx={{ fontSize: 18 }} className="mr-1" />
                    Filter
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="ml-auto">
                        <ViewColumnIcon sx={{ fontSize: 18 }} className="mr-1" />
                          Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {table
                        .getAllColumns()
                        .filter((column) => column.getCanHide())
                        .map((column) => {
                          return (
                            <DropdownMenuCheckboxItem
                              key={column.id}
                              className="capitalize"
                              checked={column.getIsVisible()}
                              onCheckedChange={(value) =>
                                column.toggleVisibility(!!value)
                              }
                            >
                              {column.columnDef.displayName}
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {showFilterButton && (
                <DataToolbar
                  table={table}
                  apiUrl="/api/get-requestsettings-filter-name"
                  updatedAt={updatedAt}
                  setUpdatedAt={setUpdatedAt}
                />
              )}
              <div className="rounded-md border">
                <Table>
                  <TableHeader className="pb-4 sticky top-0 bg-white z-[1]">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          {loading ? <TableSkeleton /> : "no records found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between space-y-2 py-1 flex-wrap gap-4">
                <div className="flex items-center gap-5">
                  <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected.
                  </div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium">Rows per page</p>
                    <Select
                      value={`${table.getState().pageSize}`}
                      onValueChange={(value) => {
                        setPageSize(Number(value));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-auto">
                        <SelectValue placeholder={table.getState().pageSize} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {[10, 20, 30, 40, 50].map((pageSize) => (
                            <SelectItem key={pageSize} value={`${pageSize}`}>
                              {pageSize}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-x-2">
                  <PaginationSection
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                    currentPage={currentPage}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentRequestSettingsTable;
